import { Dialog, DialogContent, DialogTitle, Box, Button, Typography, Alert } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useConnectionWizard } from '../hooks/useConnectionWizard';

export const ConnectionErrorDialog = () => {
  const { isOpen, connectionStatus, errorType, errorMessage, clearError, setConnectionStatus, selectConnectionType, close } = useConnectionWizard();

  const isFailed = connectionStatus === 'failed' && errorType !== null;

  const handleTryAgain = async () => {
    clearError();
    setConnectionStatus('selecting');

    // Preserve whichever transport actually failed — hardcoding 'usb' here would
    // silently reassign a failed Bluetooth attempt back to USB, so a "Try again"/"Check
    // again" click after a Bluetooth failure would pop the WebUSB picker instead of
    // retrying Bluetooth. Re-select the same value rather than force one.
    const { connectionType } = useConnectionWizard.getState();
    if (connectionType) {
      selectConnectionType(connectionType);
    }
  };

  const handleCancel = () => {
    clearError();
    close();
  };

  if (!isFailed) {
    return null;
  }

  const getErrorTitle = () => {
    switch (errorType) {
      case 'no-device-found':
        return 'No device found';
      case 'connection-failed':
        return 'Connection failed';
      case 'health-check-failed':
        return 'Device not ready';
      default:
        return 'Error';
    }
  };

  const getErrorContent = () => {
    switch (errorType) {
      case 'no-device-found':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              Please ensure the device is:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>
                <Typography variant="body2">Plugged in via USB cable</Typography>
              </li>
              <li>
                <Typography variant="body2">Powered on</Typography>
              </li>
              <li>
                <Typography variant="body2">Not connected to another computer</Typography>
              </li>
            </Box>
          </Box>
        );
      case 'connection-failed':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              Troubleshooting tips:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>
                <Typography variant="body2">Unplug and replug the USB cable</Typography>
              </li>
              <li>
                <Typography variant="body2">Restart the device</Typography>
              </li>
              <li>
                <Typography variant="body2">Try a different USB port</Typography>
              </li>
            </Box>
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="caption">Error: {errorMessage}</Typography>
              </Alert>
            )}
          </Box>
        );
      case 'health-check-failed':
        return (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">{errorMessage}</Typography>
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please address the issue above and try again.
            </Typography>
          </Box>
        );
      default:
        return (
          <Typography variant="body2">
            An unexpected error occurred. Please try again.
          </Typography>
        );
    }
  };

  return (
    <Dialog 
      open={isOpen && isFailed} 
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 2,
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <WarningAmberIcon 
            sx={{ 
              fontSize: 48, 
              color: 'warning.main',
            }} 
          />
          <Typography variant="h6" component="div">
            {getErrorTitle()}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          {getErrorContent()}
        </Box>

        <Box display="flex" flexDirection="column" gap={1.5}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleTryAgain}
            sx={{ 
              height: 48,
              borderRadius: 3,
            }}
          >
            {errorType === 'health-check-failed' ? 'Check again' : 'Try again'}
          </Button>
          
          <Button
            variant="text"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
